'use client';
import { useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import Link from 'next/link';

export default function PrivacyPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <SectionHeader
              eyebrow="Legal"
              title="Privacy Policy"
              description="Last updated: February 18, 2026"
              align="center"
              className="mb-16"
            />
          </div>
        </section>

        <section className="py-24 px-6">
          <div className="max-w-[900px] mx-auto">
            <div className="space-y-12">
              {/* Introduction */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">Introduction</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  Best Odds Corp ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                </p>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  By accessing or using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
                </p>
              </div>

              {/* Data Collection */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">1. Information We Collect</h2>
                
                <h3 className="text-lg font-semibold mb-3 text-primary">1.1 Personal Information</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-6">
                  <li>Register for an account or use social sign-in (Google, Facebook)</li>
                  <li>Request information about our services</li>
                  <li>Subscribe to our newsletter or marketing communications</li>
                  <li>Contact us through forms, email, or phone</li>
                  <li>Engage our services or enter into a contract</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  This information may include: name, email address, phone number, company name, job title, billing address, payment information, and any other information you choose to provide.
                </p>

                <h3 className="text-lg font-semibold mb-3 text-primary">1.2 Usage Data</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We automatically collect certain information when you visit our website, including:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-6">
                  <li>IP address, browser type, and operating system</li>
                  <li>Pages visited, time spent on pages, and navigation paths</li>
                  <li>Referring website addresses and search terms used</li>
                  <li>Device information and unique device identifiers</li>
                  <li>Geolocation data (with your consent)</li>
                </ul>

                <h3 className="text-lg font-semibold mb-3 text-primary">1.3 Cookies and Tracking Technologies</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  We use cookies, web beacons, pixel tags, and similar tracking technologies to collect information about your browsing activities. For detailed information, please see our <Link href="/legal/cookies" className="text-primary hover:underline">Cookie Policy</Link>.
                </p>
              </div>

              {/* How We Use Data */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">2. How We Use Your Information</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We use the information we collect for the following purposes:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4">
                  <li><strong>Service Delivery:</strong> To provide, maintain, and improve our marketing and creative services</li>
                  <li><strong>Communication:</strong> To respond to inquiries, send updates, and provide customer support</li>
                  <li><strong>Transactions:</strong> To process payments, send invoices, and manage billing</li>
                  <li><strong>Marketing:</strong> To send promotional materials, newsletters, and information about our services (with your consent)</li>
                  <li><strong>Analytics:</strong> To analyze usage patterns, improve website functionality, and optimize user experience</li>
                  <li><strong>Legal Compliance:</strong> To comply with legal obligations, resolve disputes, and enforce our agreements</li>
                  <li><strong>Security:</strong> To detect, prevent, and address fraud, security issues, and technical problems</li>
                  <li><strong>Business Operations:</strong> To conduct internal research, data analysis, and business development</li>
                </ul>
              </div>

              {/* Third-Party Sharing */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">3. Third-Party Sharing and Disclosure</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We do not sell, rent, or trade your personal information to third parties for their marketing purposes. However, we may share your information with the following categories of third parties:
                </p>

                <h3 className="text-lg font-semibold mb-3 text-primary">3.1 Service Providers and Contractors</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We engage third-party service providers to perform functions on our behalf, including:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-6">
                  <li><strong>Independent Contractors:</strong> 1099 contractors, freelancers, and specialists who assist with project delivery</li>
                  <li><strong>Freelance Platforms:</strong> Professionals sourced through platforms such as Upwork, Fiverr, and similar marketplaces</li>
                  <li><strong>Partner Agencies:</strong> External marketing agencies, creative studios, and consulting firms</li>
                  <li><strong>Technical Service Providers:</strong> Web hosting, cloud storage, email delivery, and IT infrastructure providers</li>
                  <li><strong>Payment Processors:</strong> Third-party payment gateways and financial institutions</li>
                  <li><strong>Analytics Providers:</strong> Google Analytics, advertising platforms, and performance tracking services</li>
                </ul>

                <h3 className="text-lg font-semibold mb-3 text-primary">3.2 AI Tools and Automation Platforms</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  In delivering our services, we may use artificial intelligence tools and automation platforms, including but not limited to:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-6">
                  <li>AI-powered content generation and editing tools</li>
                  <li>Machine learning platforms for data analysis and insights</li>
                  <li>Automated marketing and communication platforms</li>
                  <li>Design and creative automation software</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-6">
                  These tools may process your data to deliver requested services. We ensure that all third-party AI providers maintain appropriate data protection standards.
                </p>

                <h3 className="text-lg font-semibold mb-3 text-primary">3.3 Legal and Compliance</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We may disclose your information when required by law or in response to:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-6">
                  <li>Legal processes, court orders, or government requests</li>
                  <li>Enforcement of our terms and conditions</li>
                  <li>Protection of our rights, property, or safety, or that of others</li>
                  <li>Investigation of fraud, security issues, or illegal activities</li>
                </ul>

                <h3 className="text-lg font-semibold mb-3 text-primary">3.4 Business Transfers</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  In the event of a merger, acquisition, reorganization, sale of assets, or bankruptcy, your information may be transferred to the successor entity.
                </p>
              </div>

              {/* Cookies */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">4. Cookies and Tracking Technologies</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We use cookies and similar technologies to enhance your experience, analyze usage, and deliver personalized content. Types of cookies we use include:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-4">
                  <li><strong>Essential Cookies:</strong> Required for website functionality and security</li>
                  <li><strong>Performance Cookies:</strong> Collect anonymous data about website usage</li>
                  <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                  <li><strong>Targeting Cookies:</strong> Deliver relevant advertising and track campaign effectiveness</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  You can control cookie preferences through your browser settings. For more information, see our <Link href="/legal/cookies" className="text-primary hover:underline">Cookie Policy</Link>.
                </p>
              </div>

              {/* User Rights */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">5. Your Rights and Choices</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  Depending on your location, you may have the following rights regarding your personal information:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-4">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
                  <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                  <li><strong>Portability:</strong> Request transfer of your data to another service provider</li>
                  <li><strong>Objection:</strong> Object to processing of your personal information for certain purposes</li>
                  <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where consent was the legal basis</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  To exercise these rights, please contact us using the information provided in the Contact section below. We will respond to your request within 30 days.
                </p>
              </div>

              {/* Data Retention */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">6. Data Retention</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Retention periods vary based on the type of data and purpose of processing.
                </p>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  For detailed information about our data retention practices, including specific retention periods for different data types, please see our <Link href="/legal/data-retention" className="text-primary hover:underline">Data Retention Policy</Link>.
                </p>
              </div>

              {/* International Transfers */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">7. International Data Transfers</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your jurisdiction.
                </p>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  When we transfer personal information internationally, we implement appropriate safeguards, including standard contractual clauses, to ensure your data is protected in accordance with this Privacy Policy and applicable data protection laws.
                </p>
              </div>

              {/* Security */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">8. Data Security</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-4">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments and vulnerability testing</li>
                  <li>Access controls and authentication mechanisms</li>
                  <li>Employee training on data protection and security</li>
                  <li>Secure backup and disaster recovery procedures</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                </p>
              </div>

              {/* Children's Privacy */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">9. Children's Privacy</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
                </p>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  If we become aware that we have collected personal information from a child under 18 without parental consent, we will take steps to delete that information from our servers.
                </p>
              </div>

              {/* Changes to Policy */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">10. Changes to This Privacy Policy</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mb-4">
                  <li>Posting the updated policy on this page with a new "Last updated" date</li>
                  <li>Sending an email notification to registered users (for significant changes)</li>
                  <li>Displaying a prominent notice on our website</li>
                </ul>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  Your continued use of our services after any changes indicates your acceptance of the updated Privacy Policy.
                </p>
              </div>

              {/* Contact */}
              <div className="border border-border rounded-lg p-8 bg-card hover:border-primary/50 transition-colors">
                <h2 className="text-2xl font-bold mb-4">11. Contact Us</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-2 text-body-sm text-muted-foreground">
                  <p><strong>Best Odds Corp</strong></p>
                  <p>4575 Dean Martin Dr. Unit 1501</p>
                  <p>Las Vegas, NV 89141</p>
                  <p>USA</p>
                  <p className="mt-4">Email: <a href="mailto:hello@bestoddscorp.com" className="text-primary hover:underline">hello@bestoddscorp.com</a></p>
                  <p>Phone: <a href="tel:+17025466337" className="text-primary hover:underline">(702) 546-6337</a></p>
                  <p className="mt-4">Contact Form: <Link href="/contact" className="text-primary hover:underline">bestoddscorp.com/contact</Link></p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}